// Firebase ID tokens on API requests, the way the admin UI's api/utils.ts does it:
// each request asks the SDK for the current token (an in-memory read while it is
// valid; the SDK refreshes it only within 30 seconds of expiry), and a 401 forces a
// refresh and replays the request once. Nothing is copied into a header ahead of
// time, so there is no stale copy to keep fresh after a sleep or a throttled tab.
//
// The SDK's own early refresh (five minutes before expiry) only runs once Firestore
// has been used, which only the marketing sign-up page does, so a token near expiry
// is refreshed here in the background instead: the request in hand still carries the
// valid cached token, and the next one finds a fresh one without waiting on the network.

// A token fetch that hangs (iOS Safari can wedge the SDK's persistence after a
// suspended tab) is given up on, rather than every request pending forever with
// nothing shown: the request goes out without a token. Rails answers that with a 401,
// but Express treats it as a signed-out visitor and answers as it would for one, so a
// signed-in person can get the anonymous view with no 401 to replay.
export const TOKEN_TIMEOUT_MS = 8000
export const EARLY_REFRESH_MS = 5 * 60 * 1000

// `work`'s result, or null when it has not settled within TOKEN_TIMEOUT_MS.
function withinTimeout (work) {
  let timer = null
  const timeout = new Promise(resolve => {
    timer = setTimeout(() => resolve(null), TOKEN_TIMEOUT_MS)
  })
  return Promise.race([work, timeout]).finally(() => clearTimeout(timer))
}

export function createTokenSource (auth) {
  // Firebase restores a persisted session asynchronously; until it has said who is
  // signed in (or that nobody is), currentUser is null for a signed-in person too.
  // Every request made before then waits for it, signed-in or not. Waited for once,
  // and given up on after TOKEN_TIMEOUT_MS, so a wedged SDK costs the requests made
  // during that wait and no others.
  let unsubscribe = () => {}
  const settled = new Promise(resolve => {
    unsubscribe = auth.onAuthStateChanged(() => resolve())
  })
  settled.then(() => unsubscribe())
  const ready = withinTimeout(settled)
  let forcedRefresh = null

  // {token, expirationTime} for the signed-in user, null when there is none.
  async function tokenResult (force) {
    if (!auth.currentUser) await ready
    const user = auth.currentUser
    if (!user) return null
    return user.getIdTokenResult(force)
  }

  // A fresh token from Firebase, shared by every caller while it runs: 401s that
  // arrive while a refresh is in flight wait on it rather than starting their own. A
  // 401 that arrives after it has finished starts another. Null when the session
  // can't be recovered.
  function forced () {
    if (!forcedRefresh) {
      const refresh = tokenResult(true).catch(error => {
        console.warn('Firebase token refresh failed:', error.message)
        return null
      })
      forcedRefresh = withinTimeout(refresh)
        .then(result => (result ? result.token : null))
        .finally(() => {
          forcedRefresh = null
        })
    }
    return forcedRefresh
  }

  // The current token: the SDK's cached one while it is valid, a refreshed one once it
  // has expired. Null when nobody is signed in or the SDK has not answered in time.
  // The early-refresh check compares the token's server-issued expiry with the device
  // clock, so a device clock running well ahead refreshes more often than it needs to.
  // Rejects when the SDK could not refresh an expired token, so the request fails
  // rather than going out anonymously for someone who is signed in.
  async function current () {
    const result = await withinTimeout(tokenResult(false))
    if (!result) return null
    if (new Date(result.expirationTime) - Date.now() < EARLY_REFRESH_MS) forced()
    return result.token
  }

  return {current, forced}
}

// Checks the token when the tab comes back into view. The refresh runs on request,
// not on a timer, so after a sleep the first request would otherwise pay for the
// refresh of a token that expired meanwhile; this pays for it before anyone clicks.
export function refreshOnReturn (tokens, doc = document) {
  doc.addEventListener('visibilitychange', () => {
    if (doc.visibilityState === 'visible') tokens.current().catch(() => {})
  })
}

// Puts the token on every request of an axios instance under `header`, replacing any
// token the caller set and removing the header when there is none. Replays a request
// once when the server answers 401, after a forced refresh. The replay goes through
// the request interceptor again, which is where it picks up the fresh token. Both
// backends also answer 401 when the person is signed in but not allowed, so those
// requests are refreshed and replayed too, and fail the second time.
export function installAuthInterceptors (instance, header, tokens) {
  instance.interceptors.request.use(async config => {
    const token = await tokens.current()
    if (token) {
      config.headers.set(header, token)
    } else {
      config.headers.delete(header)
    }
    return config
  })

  instance.interceptors.response.use(undefined, async error => {
    const config = error.config
    if (!config || !error.response || error.response.status !== 401 || config.authReplayed) {
      throw error
    }
    const token = await tokens.forced()
    if (!token) throw error
    config.authReplayed = true
    return instance.request(config)
  })
}
